import React from 'react'
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts'
import { ChartCard, TooltipBox } from './AIChartParts'
import { PROVIDER_LABELS, GRID, AXIS_TEXT, usd, num, shortDate, longDate, axisUsd, providerLabel, providerColor } from './aiCostFormat'

/** Providers found in the range, known ones first so their bar order stays the same */
const providersIn = (daily) => {
  const found = new Set(daily.flatMap((d) => Object.keys(d.providers || {})))
  return [...Object.keys(PROVIDER_LABELS).filter((p) => found.has(p)), ...[...found].filter((p) => !PROVIDER_LABELS[p])]
}

const CostTooltip = ({ active, payload, label, providers }) => {
  if (!active || !payload?.length) return null
  const d = payload[0].payload
  const rows = providers.map((p) => ({
    label: providerLabel(p),
    value: `${usd(d.providers?.[p]?.cost)} · ${num(d.providers?.[p]?.requests)} req`,
    color: providerColor(p),
  }))
  if (providers.length > 1) rows.push({ label: 'Total', value: usd(d.cost) })
  rows.push({ label: 'Requests', value: num(d.requests) })
  return <TooltipBox title={longDate(label)} rows={rows} />
}

/** Daily cost, providers' bars side by side per day */
const AICostTrendChart = ({ daily = [], isLoading, error }) => {
  const providers = providersIn(daily)
  return (
    <ChartCard
      title="Daily Cost Trend"
      subtitle={`${providers.length ? providers.map(providerLabel).join(' + ') : 'AI'} USD per day`}
      isLoading={isLoading}
      error={error}
      isEmpty={!daily.some((d) => d.cost > 0)}
    >
      <ResponsiveContainer width="100%" height={280}>
        <BarChart data={daily} margin={{ top: 8, right: 8, left: 0, bottom: 0 }} barCategoryGap="20%" barGap={2}>
          <CartesianGrid vertical={false} stroke={GRID} />
          <XAxis dataKey="date" tickFormatter={shortDate} tick={{ fill: AXIS_TEXT, fontSize: 12 }} axisLine={false} tickLine={false} minTickGap={16} />
          <YAxis tickFormatter={axisUsd} tick={{ fill: AXIS_TEXT, fontSize: 12 }} axisLine={false} tickLine={false} width={60} />
          <Tooltip cursor={{ fill: 'rgba(0,0,0,0.04)' }} content={<CostTooltip providers={providers} />} />
          {providers.length > 1 && <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 12 }} />}
          {providers.map((p) => (
            <Bar
              key={p}
              dataKey={(d) => d.providers?.[p]?.cost || 0}
              name={providerLabel(p)}
              fill={providerColor(p)}
              radius={[4, 4, 0, 0]}
              maxBarSize={16}
            />
          ))}
        </BarChart>
      </ResponsiveContainer>
    </ChartCard>
  )
}

export default AICostTrendChart
